import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Grid, Chip, Divider, Button, TextField, MenuItem,
    Avatar, Card, CardContent, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import { ArrowBack, Send, CheckCircle, Info, Warning, BugReport } from '@mui/icons-material';
import * as actionTypes from '../../redux/actionTypes';

const STATUS_COLORS = { Created: '#2196F3', Review: '#FF9800', Working: '#9C27B0', Closed: '#4CAF50' };
const TYPE_COLORS = { Bug: '#f44336', 'Feature Request': '#2196F3', Enhancement: '#4CAF50', Crash: '#FF5722', 'UI Problem': '#FF9800' };
const NEXT_STATUS = { Created: 'Review', Review: 'Working', Working: 'Closed' };
const STATUS_ICONS = { Created: <Info />, Review: <Warning />, Working: <BugReport />, Closed: <CheckCircle /> };

const IssueDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const issue = useSelector(state => state.issueReducer?.currentIssue);

    const [comment, setComment] = useState('');
    const [adminName, setAdminName] = useState('');
    const [statusComment, setStatusComment] = useState('');

    useEffect(() => {
        if (id) dispatch({ type: actionTypes.GET_ISSUE_BY_ID, payload: id });
    }, [dispatch, id]);

    const handleAddComment = () => {
        if (!comment.trim() || !adminName.trim()) return;
        dispatch({
            type: actionTypes.ADD_ISSUE_COMMENT,
            payload: { id, data: { text: comment, adminName, adminRole: 'Admin' } }
        });
        setComment('');
    };

    const handleStatusChange = () => {
        const nextStatus = NEXT_STATUS[issue?.status];
        if (!nextStatus || !statusComment.trim() || !adminName.trim()) return;
        dispatch({
            type: actionTypes.CHANGE_ISSUE_STATUS,
            payload: { id, data: { status: nextStatus, changedBy: adminName, comment: statusComment } }
        });
        setStatusComment('');
    };

    if (!issue) return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h5" color="text.secondary">Loading issue details...</Typography>
        </Box>
    );

    const nextStatus = NEXT_STATUS[issue.status];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ borderRadius: 2, textTransform: 'none' }}>Back</Button>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Chip label={issue.issueId} sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 700, fontSize: '0.9rem' }} />
                        <Chip label={issue.status} sx={{ bgcolor: STATUS_COLORS[issue.status], color: '#fff', fontWeight: 600 }} />
                        <Chip label={issue.type} variant="outlined" sx={{ borderColor: TYPE_COLORS[issue.type], color: TYPE_COLORS[issue.type], fontWeight: 500 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>{issue.title}</Typography>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Left Column - Details */}
                <Grid item xs={12} md={8}>
                    {/* Description */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>📋 Description</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{issue.description}</Typography>

                        {issue.expectedResult && (
                            <Box sx={{ mt: 2.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4CAF50', mb: 0.5 }}>✅ Expected Result</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{issue.expectedResult}</Typography>
                            </Box>
                        )}

                        {issue.steps && issue.steps.length > 0 && (
                            <Box sx={{ mt: 2.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#FF9800', mb: 1 }}>🔢 Steps to Reproduce</Typography>
                                <List dense>
                                    {issue.steps.map((step, i) => (
                                        <ListItem key={i} sx={{ py: 0.3 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <Avatar sx={{ width: 22, height: 22, fontSize: '0.75rem', bgcolor: '#1976d2' }}>{i + 1}</Avatar>
                                            </ListItemIcon>
                                            <ListItemText primary={step} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Paper>

                    {/* Status Change */}
                    {nextStatus && (
                        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: `2px solid ${STATUS_COLORS[nextStatus]}20` }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: STATUS_COLORS[nextStatus] }}>
                                🔄 Move to → {nextStatus}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth size="small" label="Your Name *" value={adminName} onChange={(e) => setAdminName(e.target.value)} />
                                </Grid>
                                <Grid item xs={12} sm={8}>
                                    <TextField fullWidth size="small" label={`Comment for ${nextStatus} *`} value={statusComment}
                                        onChange={(e) => setStatusComment(e.target.value)} placeholder={`Why moving to ${nextStatus}?`} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button variant="contained" onClick={handleStatusChange}
                                        disabled={!statusComment.trim() || !adminName.trim()}
                                        sx={{ bgcolor: STATUS_COLORS[nextStatus], borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: STATUS_COLORS[nextStatus], filter: 'brightness(0.9)' } }}>
                                        Change Status: {issue.status} → {nextStatus}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                    {issue.status === 'Closed' && (
                        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: '#E8F5E9', border: '2px solid #4CAF50' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>✅ This issue is CLOSED</Typography>
                        </Paper>
                    )}

                    {/* Comments Section */}
                    <Paper sx={{ p: 3, borderRadius: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>💬 Comments ({issue.comments?.length || 0})</Typography>

                        {/* Add Comment */}
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                            <TextField size="small" label="Your Name" value={adminName} onChange={(e) => setAdminName(e.target.value)} sx={{ width: 180 }} />
                            <TextField fullWidth size="small" label="Add a comment..." value={comment} onChange={(e) => setComment(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} />
                            <Button variant="contained" startIcon={<Send />} onClick={handleAddComment} disabled={!comment.trim() || !adminName.trim()}
                                sx={{ borderRadius: 2, textTransform: 'none', minWidth: 100 }}>Send</Button>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Comment List */}
                        {issue.comments && issue.comments.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {[...issue.comments].reverse().map((c, i) => (
                                    <Card key={i} variant="outlined" sx={{ borderRadius: 2, bgcolor: c.statusChange ? '#FFF3E0' : '#f9f9f9' }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: c.statusChange ? '#FF9800' : '#1976d2' }}>
                                                        {c.adminName?.charAt(0)?.toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{c.adminName}</Typography>
                                                    {c.adminRole && <Chip label={c.adminRole} size="small" sx={{ fontSize: '0.6rem', height: 18 }} />}
                                                    {c.statusChange && <Chip label={`Status → ${c.statusChange}`} size="small" sx={{ bgcolor: STATUS_COLORS[c.statusChange], color: '#fff', fontSize: '0.6rem', height: 18 }} />}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">{new Date(c.createdAt).toLocaleString('en-IN')}</Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ ml: 4.5 }}>{c.text}</Typography>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No comments yet. Be the first to comment!</Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Right Column - Meta Info */}
                <Grid item xs={12} md={4}>
                    {/* Info Card */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>📌 Issue Info</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <InfoRow label="Issue ID" value={issue.issueId} />
                            <InfoRow label="Module" value={issue.module} />
                            <InfoRow label="Platform" value={issue.platform?.join(', ')} />
                            <InfoRow label="Frequency" value={issue.frequency || 'N/A'} />
                            <InfoRow label="Reproducible" value={issue.reproducible ? 'Yes' : 'No'} />
                            <InfoRow label="Created By" value={issue.createdBy} />
                            <InfoRow label="Created" value={new Date(issue.createdAt).toLocaleString('en-IN')} />
                            <InfoRow label="Updated" value={new Date(issue.updatedAt).toLocaleString('en-IN')} />
                        </Box>
                    </Paper>

                    {/* Device Info */}
                    {issue.deviceInfo && (issue.deviceInfo.deviceName || issue.deviceInfo.osVersion) && (
                        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>📱 Device Info</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {issue.deviceInfo.deviceName && <InfoRow label="Device" value={issue.deviceInfo.deviceName} />}
                                {issue.deviceInfo.osVersion && <InfoRow label="OS" value={issue.deviceInfo.osVersion} />}
                                {issue.deviceInfo.appVersion && <InfoRow label="App Version" value={issue.deviceInfo.appVersion} />}
                                {issue.deviceInfo.browser && <InfoRow label="Browser" value={issue.deviceInfo.browser} />}
                            </Box>
                        </Paper>
                    )}

                    {/* Status Timeline */}
                    <Paper sx={{ p: 3, borderRadius: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>📊 Status History</Typography>
                        {issue.statusHistory && issue.statusHistory.length > 0 ? (
                            <Box>
                                {[...issue.statusHistory].reverse().map((h, i) => (
                                    <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2, pb: 2, borderBottom: i < issue.statusHistory.length - 1 ? '1px solid #eee' : 'none' }}>
                                        <Avatar sx={{ width: 30, height: 30, bgcolor: STATUS_COLORS[h.to], fontSize: '0.7rem' }}>
                                            {h.to?.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                {h.from && <Chip label={h.from} size="small" sx={{ fontSize: '0.6rem', height: 18 }} />}
                                                {h.from && <Typography variant="caption">→</Typography>}
                                                <Chip label={h.to} size="small" sx={{ bgcolor: STATUS_COLORS[h.to], color: '#fff', fontSize: '0.6rem', height: 18 }} />
                                            </Box>
                                            <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.3 }}>{h.changedBy} • {new Date(h.createdAt).toLocaleString('en-IN')}</Typography>
                                            <Typography variant="caption" sx={{ color: '#999' }}>{h.comment}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">No status changes yet</Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

const InfoRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{value || 'N/A'}</Typography>
    </Box>
);

export default IssueDetails;
